import "./PeakHoursHeatmap.css";

/**
 * Phase 18 WU-18.E2 — 7×24 peak-hours heatmap.
 *
 * Cells shade from background → Safety Orange proportional to the busiest
 * cell in the grid. Always renders 168 cells for a stable layout.
 */
export interface PeakHourCell {
  dow: number; // 0=Sun..6=Sat
  hour: number; // 0..23
  count: number;
}

export interface PeakHoursHeatmapProps {
  grid: PeakHourCell[];
}

const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function bucketizeIntensity(count: number, max: number): string {
  if (max <= 0 || count <= 0) return "peak-cell--empty";
  const ratio = count / max;
  if (ratio >= 0.85) return "peak-cell--full";
  if (ratio >= 0.6) return "peak-cell--high";
  if (ratio >= 0.35) return "peak-cell--med";
  if (ratio >= 0.15) return "peak-cell--low";
  return "peak-cell--min";
}

export function PeakHoursHeatmap({ grid }: PeakHoursHeatmapProps) {
  const max = grid.reduce((m, c) => Math.max(m, c.count), 0);

  if (max === 0) {
    return (
      <div
        className="peak-hours__empty"
        data-testid="peak-hours-empty"
      >
        No activity in this period.
      </div>
    );
  }

  const byKey = new Map<string, number>();
  for (const c of grid) {
    byKey.set(`${c.dow}-${c.hour}`, c.count);
  }

  return (
    <div className="peak-hours" data-testid="peak-hours">
      <div className="peak-hours__hours-row">
        <div className="peak-hours__dow-spacer" />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="peak-hours__hour-label">
            {h % 3 === 0 ? h : ""}
          </div>
        ))}
      </div>
      {DOW_LABELS.map((label, dow) => (
        <div key={dow} className="peak-hours__row">
          <div className="peak-hours__dow-label">{label}</div>
          {Array.from({ length: 24 }, (_, hour) => {
            const count = byKey.get(`${dow}-${hour}`) ?? 0;
            const klass = bucketizeIntensity(count, max);
            return (
              <div
                key={hour}
                className={`peak-cell ${klass}`}
                data-testid={`peak-cell-${dow}-${hour}`}
                title={`${DOW_LABELS[dow]} ${hour}:00 — ${count} docs`}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
